"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePowerUsageDaily = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Hook to fetch daily power usage data for a specific sensor
 * @param params - Parameters for fetching power usage data
 * @returns Power usage data, loading state, error, and refetch function
 */
const usePowerUsageDaily = ({ sensorId, startDate, endDate, enabled = true, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchPowerUsageData = async () => {
        if (!enabled || !sensorId || !startDate || !endDate) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId,
                    startDate,
                    endDate,
                    nextToken: undefined, // For now, we'll handle pagination later if needed
                },
            });
            const rawData = result?.data?.getSensorUsageRollup;
            const sensorUsageData = rawData?.items || rawData || [];
            if (sensorUsageData && Array.isArray(sensorUsageData) && sensorUsageData.length > 0) {
                // Map SensorUsageRollup to PowerSensorUsageDaily and handle empty or invalid data
                const powerUsageData = sensorUsageData
                    .filter((item) => item && item.powerUsageRollup) // Filter items with power usage data
                    .map((item) => ({
                    sensorId: item.sensorId,
                    date: item.date,
                    hubId: item.hubId,
                    totalDataPoints: item.totalDataPoints,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                    // Spread the power usage data properties
                    ...item.powerUsageRollup,
                }));
                // Sort by date in ascending order for consistent data processing
                const sortedData = powerUsageData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                setData(sortedData);
            }
            else {
                setData([]);
            }
        }
        catch (err) {
            console.error('Error fetching power usage data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch power usage data');
            setData(null);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchPowerUsageData();
    };
    (0, react_1.useEffect)(() => {
        fetchPowerUsageData();
    }, [sensorId, startDate, endDate, enabled]);
    return {
        data,
        loading,
        error,
        refetch,
    };
};
exports.usePowerUsageDaily = usePowerUsageDaily;
